import React, { ComponentProps } from 'react'
import { Spin } from 'antd'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useCalculationTimepointsExtraValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { getColorByIndex } from '../../util/colors'
import { getFontSize } from '../../util/fontSize'
import { BORDER_WIDTH } from '../../util/constants'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const TITLE = 'Tidpunktsprevalens per kommun'

export const TimepointPrevalence: React.FC<{ text: React.ReactNode }> = ({ text }) => {
  const { data, isLoading, error } = useCalculationTimepointsExtraValues()

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} title={TITLE} />
  }

  if (error || !data) {
    return (
      <BenchmarkingContentRow
        title={TITLE}
        text={text}
        graph={
          <div className="flex h-full w-full items-center justify-center font-mono text-sm text-red-600">
            <span>{error ? `Fel vid hämtning av data: ${String(error)}` : 'Ingen data tillgänglig'}</span>
          </div>
        }
      />
    )
  }

  const timepoints = Array.from(
    new Map(data.map((r) => [r.timepoint, r.sort_order] as [string, number])).entries()
  ).sort((a, b) => a[1] - b[1]).map(([tp]) => tp)

  const municipalities = Array.from(new Set(data.map((r) => r.municipality_name as string))).sort()

  // Kommun som serie: getColorByIndex cyklar COLORS-paletten (index 0 = blå),
  // samma fill+border-stil som Volyms staplar.
  const datasets = municipalities.map((name, i) => {
    const color = getColorByIndex(i)
    return {
      label: name as string,
      backgroundColor: color.fill,
      borderColor: color.border,
      borderWidth: BORDER_WIDTH,
      data: timepoints.map((tp) => {
        const row = data.find((r) => r.municipality_name === name && r.timepoint === tp)
        return row ? Number(row.prevalence_pct) : 0
      }),
    }
  })

  const options: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: getFontSize('md') } },
      },
      y: {
        min: 0,
        max: 100,
        ticks: { font: { size: getFontSize('md') }, callback: (v) => `${v}%` },
        title: { display: true, text: 'Andel brukare (%)', font: { size: getFontSize('md') } },
      },
    },
  }

  const legend = (
    <BenchmarkingLegend
      items={municipalities.map((name, i) => {
        const color = getColorByIndex(i)
        return { text: name, fillColor: color.fill, borderColor: color.border }
      })}
    />
  )

  return <BenchmarkingContentRow title={TITLE} text={text} legend={legend} graph={<Bar data={{ labels: timepoints, datasets }} options={options} />} />
}
